import React from "react";
import { MOBILE_SEARCH } from "common/constants";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Text from "common/Typography/Text";
import Popover from "common/Popover/Popover";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import ExclamationCircleOutlined from "@ant-design/icons/ExclamationCircleOutlined";
import WarningOutlined from "@ant-design/icons/WarningOutlined";
import useMedia from "utils/hooks/useMedia";
import { OrderSummaryProps } from "@shared/features/bots/BotForm/common/types";
import useOrderSummary from "@shared/features/bots/BotForm/OrdersSummary/useOrderSummary";
import TextWithIcon from "common/Text/TextWithIcon";
import Flex from "common/Boxes/Flex";
import { typography } from "@shared/common/Themes/typography";
import styled from "styled-components";
import OrderNumber from "common/Numbering/OrderNumber";

const OrderSummary = ({
  order,
  index,
  spans,
  showAmount,
  showPrice,
  firstOrderDistanceText,
  orderType,
  hideAmountColumn,
  isVerticalCell,
}: OrderSummaryProps) => {
  const isSmallText = useMedia(
    [`(max-width: ${MOBILE_SEARCH})`],
    [true],
    false
  );
  const {
    priceData,
    amountTextType,
    amountData,
    errorData,
    showWarningIcon,
    showIsImmediate,
    isImmediate,
    isTradingViewSignalsBot,
    useSymbolFromAlert,
  } = useOrderSummary({
    order,
    index,
    showAmount,
    showPrice,
    firstOrderDistanceText,
    orderType,
  });

  const getAmount = () => {
    if (hideAmountColumn) return;
    if (isTradingViewSignalsBot || useSymbolFromAlert) return amountData.ratio;
    if (amountData.text) return amountData.text;
    else if (showAmount) {
      return (
        <CellWrapper $isMobile={isVerticalCell}>
          <TextWithIcon text={amountData.amount} />
          {amountData.ratio && <span>({amountData.ratio})</span>}
        </CellWrapper>
      );
    }
    return amountData.ratio;
  };

  const getPrice = () => {
    if (showPrice) {
      return (
        <CellWrapper $isMobile={isVerticalCell}>
          <span>{priceData.price}</span>
          <span>{priceData.distance}</span>
        </CellWrapper>
      );
    }
    return priceData.distance;
  };

  const getError = () => {
    if (!errorData) return;
    return (
      <>
        <Text $size={typography.fontSizes.S} type="secondary">
          {errorData.title}
        </Text>
        {errorData.error}
      </>
    );
  };

  const getIsImmediate = () => {
    if (!showIsImmediate) return;
    return (
      <Col
        span={spans[1]}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text type={isImmediate ? "primary" : "secondary"}>
          {isImmediate ? <CheckOutlined /> : "-"}
        </Text>
      </Col>
    );
  };

  const error = getError();

  return (
    <Wrapper gutter={[4, 4]} $isMobile={isSmallText}>
      <Col span={spans[0]} style={{ display: "flex", alignItems: "center" }}>
        <Flex style={{ marginLeft: -12 }}>
          <OrderNumber displayNumber={index + 1} />
          <Text as="div">{getPrice()}</Text>
        </Flex>
      </Col>
      <Col span={spans[1]}>
        <Text
          type={amountTextType}
          as="div"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginRight: showIsImmediate ? 8 : 0,
            fontSize: "inherit",
          }}
        >
          {getAmount()}
          {error && (
            <Popover content={error} placement="topRight">
              <ExclamationCircleOutlined />
            </Popover>
          )}
          {!error && showWarningIcon && <WarningOutlined />}
        </Text>
      </Col>
      {getIsImmediate()}
    </Wrapper>
  );
};

export default OrderSummary;

const Wrapper = styled(Row)<{
  $isMobile?: boolean;
}>`
  width: 100%;
  padding: 2px 12px;
  ${(p) =>
    p.$isMobile &&
    `
    font-size: ${p.theme.typography.fontSizes.S};
  `};
  ${(p) =>
    p.theme.isNewLook &&
    `
    padding: 7px 12px;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
    &:not(:last-child) {
      border-bottom: ${p.theme.borders.lightest};
    }
  `};
`;
const CellWrapper = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  align-items: baseline;
  column-gap: 4px;
  ${(p) =>
    p.$isMobile &&
    `
    flex-direction: column;
  `};
  ${(p) =>
    p.theme.isNewLook &&
    `
    column-gap: 6px;
    > * + * {
      color: ${p.theme.colors.secondaryText};
      font-size: 12.5px;
    }
  `};
`;
